import { ChangeDetectionStrategy, Component, EventEmitter, Output, signal } from '@angular/core';

// NEAT optional deal-context capture (mirrors the old portal). Self-contained:
// owns its prompt/form UI, field state, and the [Deal Context] preamble it
// builds. The host (agenthome-chat) only shows it for NEAT with a staged
// transcript and listens for `analyze`. Keeping it here — not in the shared chat
// component — is deliberate: NEAT-specific behaviour stays in a NEAT-owned
// location.
//
// Fields are NOT pre-filled across analyses. Rep is stable per user, but
// Account / Deal stage / Opportunity are deal-specific — carrying a prior deal's
// values into a new transcript is misleading and (since the runtime reconciles
// the Account into the analysis verbatim) can produce an analysis under the
// wrong account if the stale value is left unnoticed. So every analysis starts
// from a blank form.

// Dropdown values MUST match the runtime's neat_analysis_schema.json enums
// exactly, or the agent's verbatim copy fails validation.
const NEAT_DEAL_STAGES = ['Discovery', 'Qualification', 'Evaluation', 'Proposal', 'Negotiation', 'Renewal'];
const NEAT_OPPORTUNITY_TYPES = ['New Business', 'Renewal', 'Upsell/Cross-sell', 'Reactivation'];

@Component({
  selector: 'app-neat-deal-context',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './neat-deal-context.component.html',
  styleUrls: ['./neat-deal-context.component.scss'],
})
export class NeatDealContextComponent {
  /**
   * Emitted when the user resolves the flow. The payload is the `[Deal Context]`
   * preamble to prepend to the analyze message — or '' when they skip / add
   * nothing. The host prepends it and sends the transcript for analysis.
   */
  @Output() analyze = new EventEmitter<string>();

  // 'prompt' = the Yes/No ask; 'form' = the optional fields. A fresh instance
  // (the host re-creates it via @if each time) always starts at 'prompt'.
  readonly stage = signal<'prompt' | 'form'>('prompt');

  readonly rep = signal('');
  readonly account = signal('');
  readonly dealStage = signal('');
  readonly opportunity = signal('');
  readonly stages = NEAT_DEAL_STAGES;
  readonly opportunities = NEAT_OPPORTUNITY_TYPES;

  onYes(): void { this.stage.set('form'); }
  onNo(): void { this.analyze.emit(''); }
  onContinue(): void {
    this.analyze.emit(this._buildPreamble());
  }

  // Signal-based field handlers (no FormsModule dependency).
  onRep(e: Event): void { this.rep.set((e.target as HTMLInputElement).value); }
  onAccount(e: Event): void { this.account.set((e.target as HTMLInputElement).value); }
  onStage(e: Event): void { this.dealStage.set((e.target as HTMLSelectElement).value); }
  onOpportunity(e: Event): void { this.opportunity.set((e.target as HTMLSelectElement).value); }

  /** Build the exact [Deal Context] block the runtime prompt expects. Blank
   *  fields become "Unknown" (a valid schema enum). Returns '' when all blank. */
  private _buildPreamble(): string {
    const rep = this.rep().trim();
    const account = this.account().trim();
    const stage = this.dealStage().trim();
    const opp = this.opportunity().trim();
    if (!rep && !account && !stage && !opp) return '';
    return `[Deal Context]\nRep: ${rep || 'Unknown'} | Account: ${account || 'Unknown'} | `
         + `Stage: ${stage || 'Unknown'} | Opportunity: ${opp || 'Unknown'}`;
  }
}
